import {
  CollapsibleTagList,
  BookmarkButton,
  ExpandableContent,
  ExternalLink,
} from '@/components/Elements';
import { Pagination } from '@/features/analyze/components/Pagination';
import {
  MetadataTag,
  TagContainer,
  ThemeTag,
} from '@/features/analyze/components/Tag';
import { TopicTagWithTooltip } from '@/features/analyze/components/TopicTagWithTooltip';
import { BookmarkLayout } from '@/features/bookmark/components/BookmarkLayout';
import { FragmentOf, graphql, readFragment, useQuery } from '@/lib/graphql';
import {
  CubeIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  HashtagIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

// BookmarkList用のItem Fragment (BookmarkButtonのFragmentを含む)
const bookmarkedArticleItemFragmentForList = graphql(`
  fragment BookmarkedArticleItemFragmentForList on ActiveArticle {
    id
    code
    title
    url
    publishedAt
    summary
    imageUrl
    country
    organizationType
    organization
    displayableTopicTags {
      lookupKey
      name
      description
      themes
      isSocialIssue
    }
    isBookmarkedByCurrentUser
    themes
  }
`);

const GET_BOOKMARKED_ARTICLES_QUERY_FOR_LIST = graphql(
  `
    query getBookmarkedArticlesForList($page: Int!, $perPage: Int!) {
      bookmarkedArticles(page: $page, perPage: $perPage) {
        items {
          ... on ActiveArticle {
            ...BookmarkedArticleItemFragmentForList
          }
          ... on DeletedArticle {
            code
            url
          }
        }
        pageInfo {
          currentPage
          totalCount
          hasNextPage
          hasPreviousPage
          lastPage
          perPage
        }
      }
    }
  `,
  [bookmarkedArticleItemFragmentForList]
);

interface BookmarkedArticleListItemProps {
  data: FragmentOf<typeof bookmarkedArticleItemFragmentForList>;
}

const BookmarkedArticleListItem: React.FC<BookmarkedArticleListItemProps> = (
  props
) => {
  const article = readFragment(
    bookmarkedArticleItemFragmentForList,
    props.data
  );

  return (
    <div className="flex gap-x-6">
      <img
        src={article.imageUrl || '/images/article-no-image.jpg'}
        alt="Article Thumbnail"
        className="hidden size-40 min-h-40 min-w-40 rounded-md object-cover lg:block"
        onError={(e) => (e.currentTarget.src = '/images/article-no-image.jpg')}
      />
      <div className="flex flex-1 flex-col gap-y-2 overflow-visible">
        <div className="flex items-start justify-between">
          <ExternalLink
            to={article.url}
            underline="hover"
            className="min-w-0 grow break-words text-lg font-semibold md:text-xl"
          >
            {article.title}
          </ExternalLink>
          <div className="ml-2 shrink-0">
            <BookmarkButton
              code={article.code}
              title={article.title}
              url={article.url}
              isBookmarked={article.isBookmarkedByCurrentUser}
            />
          </div>
        </div>
        <TagContainer>
          {article.themes && article.themes.length > 0 && (
            <CollapsibleTagList
              items={article.themes}
              maxVisibleItems={3}
              textColor="text-rw-sub"
              renderTag={(theme) => (
                <ThemeTag key={theme} icon={HashtagIcon} tableName={theme} />
              )}
            />
          )}
          {article.country && (
            <MetadataTag icon={GlobeAltIcon} text={article.country} />
          )}
          {article.organizationType && (
            <MetadataTag icon={CubeIcon} text={article.organizationType} />
          )}
          {article.organization && <MetadataTag text={article.organization} />}
          {article.displayableTopicTags &&
            article.displayableTopicTags.length > 0 && (
              <CollapsibleTagList
                items={article.displayableTopicTags}
                maxVisibleItems={10}
                textColor="text-rw-black"
                renderTag={(tag) => (
                  <TopicTagWithTooltip
                    icon={TagIcon}
                    key={tag.lookupKey}
                    name={tag.name}
                    description={tag.description}
                    themes={tag.themes}
                    isSocialIssue={tag.isSocialIssue}
                  />
                )}
              />
            )}
        </TagContainer>
        <ExpandableContent aria-label="記事サマリ" className="line-clamp-3">
          {`${new Date(article.publishedAt).toLocaleDateString()} - ${article.summary ?? ''}`}
        </ExpandableContent>
      </div>
    </div>
  );
};

export const BookmarkList = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const perPage = 10;
  const { data, loading } = useQuery(GET_BOOKMARKED_ARTICLES_QUERY_FOR_LIST, {
    variables: { page, perPage },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  if (loading) {
    return <BookmarkLayout totalCount={0} />;
  }

  if (
    data?.bookmarkedArticles?.items == null ||
    data.bookmarkedArticles.items.length === 0
  ) {
    return (
      <BookmarkLayout totalCount={0}>
        <div className="text-gray-500">
          {t('ブックマークはありません')}
          <br />
          {t('お気に入りの記事をブックマークに追加しましょう。')}
        </div>
      </BookmarkLayout>
    );
  }

  const { items, pageInfo } = data.bookmarkedArticles;

  return (
    <BookmarkLayout totalCount={pageInfo.totalCount}>
      {items.map((item, index) => {
        if (item.__typename === 'ActiveArticle') {
          return <BookmarkedArticleListItem key={String(index)} data={item} />;
        } else if (item.__typename === 'DeletedArticle') {
          return (
            <div key={String(index)} className="flex gap-x-6">
              <div className="hidden size-40 min-h-40 min-w-40 items-center justify-center rounded-md bg-gray-200 lg:flex">
                <ExclamationTriangleIcon className="size-16 text-gray-400" />
              </div>
              <div className="flex flex-1 flex-col gap-y-2">
                <h3 className="text-lg font-medium text-gray-700">
                  {t('RuleWatcherから記事が削除されました')}
                </h3>
                <p className="text-gray-600">
                  <Trans
                    i18nKey="deleted-article-link-text"
                    components={{
                      l: <ExternalLink to={item.url} />,
                    }}
                  />
                </p>
              </div>
            </div>
          );
        }
        return null;
      })}

      <div className="mt-10 flex items-center justify-center">
        <Pagination
          pageInfo={{
            currentPage: pageInfo.currentPage,
            perPage: pageInfo.perPage,
            totalCount: pageInfo.totalCount,
            hasNextPage: pageInfo.hasNextPage,
            hasPreviousPage: pageInfo.hasPreviousPage,
            lastPage: pageInfo.lastPage,
          }}
          onClick={(offset) =>
            setPage(Math.floor(offset / pageInfo.perPage) + 1)
          }
          size="md"
          className="justify-center"
        />
      </div>
    </BookmarkLayout>
  );
};
